// user-dashboard/components/ConvertToTtOtpModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const SELECTED_PREVIEW_LIMIT = 5;

const BUYER_CONFIRMATION_POINTS = [
  {
    key: "sim_in_port",
    single: "I confirm this SIM is currently in the port and the number is working.",
    bulk: "I confirm these SIMs are currently in their ports and the numbers are working.",
  },
  {
    key: "understand_conversion",
    single: "I understand this number will be converted to a TT OTP SIM.",
    bulk: "I understand these numbers will be converted to TT OTP SIMs.",
  },
  {
    key: "leave_sim",
    single: "I will not remove or swap this SIM until conversion is complete.",
    bulk: "I will not remove or swap these SIMs until conversion is complete.",
  },
  {
    key: "want_to_proceed",
    single: "I want staff to process this conversion request.",
    bulk: "I want staff to process these conversion requests.",
  },
];

function nationalPhoneDigits(value) {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.startsWith("44") && digits.length >= 11) digits = digits.slice(2);
  if (digits.startsWith("0") && digits.length >= 10) digits = digits.slice(1);
  return digits;
}

function portMatchesSearch(port, query) {
  const raw = (query || "").trim().toLowerCase();
  if (!raw) return false;
  const email = (port?.user_email || "").toLowerCase();
  const number = String(port?.assigned_number || "");
  const digits = number.replace(/\D/g, "");
  const qDigits = raw.replace(/\D/g, "");
  if (email.includes(raw)) return true;
  if (number.toLowerCase().includes(raw)) return true;
  if (qDigits && digits.includes(qDigits)) return true;
  const nq = nationalPhoneDigits(qDigits);
  const nd = nationalPhoneDigits(digits);
  if (nq && nd && (nd.includes(nq) || nq.includes(nd))) return true;
  return false;
}

function PortPickLabel({ port, details = false }) {
  if (!details) {
    return <span className="small">{port.assigned_number || "—"}</span>;
  }
  return (
    <span className="small">
      <strong>{port.assigned_number || "—"}</strong>
      {port.port_number ? (
        <span className="text-muted"> · port {port.port_number}</span>
      ) : null}
      {port.user_email ? (
        <div className="text-muted">{port.user_email}</div>
      ) : null}
    </span>
  );
}

/**
 * Confirm Convert to TT OTP.
 * Buyer: details + required checkboxes before Confirm is enabled.
 * Staff: details + Cancel / Confirm.
 * Bulk: search by number/email, select ports (unchecked by default), then confirm.
 */
export default function ConvertToTtOtpModal({
  open,
  onClose,
  port = null,
  ports = null,
  record = null,
  isStaff = false,
  bulk = false,
  onConfirm,
}) {
  const portList = useMemo(() => {
    if (Array.isArray(ports) && ports.length) return ports.filter(Boolean);
    return port ? [port] : [];
  }, [ports, port]);

  const isBulk = bulk || portList.length > 1;
  const [checked, setChecked] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllSelected, setShowAllSelected] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const selectAllRef = useRef(null);

  const portListKey = portList.map((p) => p.id).join(",");

  useEffect(() => {
    if (!open) return;
    setChecked({});
    setSubmitting(false);
    setError(null);
    setSelectedIds([]);
    setSearchQuery("");
    setShowAllSelected(false);
  }, [open, port?.id, portListKey]);

  const portsById = useMemo(() => {
    const map = new Map();
    for (const p of portList) {
      if (p?.id != null) map.set(p.id, p);
    }
    return map;
  }, [portList]);

  const selectedPorts = selectedIds
    .map((id) => portsById.get(id))
    .filter(Boolean);

  const visibleSelected = showAllSelected
    ? selectedPorts
    : selectedPorts.slice(0, SELECTED_PREVIEW_LIMIT);
  const extraSelectedCount = Math.max(0, selectedPorts.length - SELECTED_PREVIEW_LIMIT);

  useEffect(() => {
    if (selectedPorts.length <= SELECTED_PREVIEW_LIMIT) setShowAllSelected(false);
  }, [selectedPorts.length]);

  const searchMatches = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return portList;
    return portList.filter((p) => portMatchesSearch(p, q));
  }, [portList, searchQuery]);

  const listedPorts = searchQuery.trim() ? searchMatches : portList;
  const listedIds = listedPorts.map((p) => p.id).filter((id) => id != null);
  const listedSelectedCount = listedIds.filter((id) => selectedIds.includes(id)).length;
  const allListedSelected = listedIds.length > 0 && listedSelectedCount === listedIds.length;
  const someListedSelected = listedSelectedCount > 0 && !allListedSelected;
  const listedLabel = searchQuery.trim()
    ? `Matches (${listedPorts.length})`
    : `Gateway numbers (${listedPorts.length})`;
  const selectAllLabel = searchQuery.trim() ? "Select all matches" : "Select all";

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someListedSelected;
    }
  }, [someListedSelected]);

  if (!open) return null;

  const stop = (e) => e.stopPropagation();
  const primary = selectedPorts[0] || portList[0] || port;
  const number = (primary?.assigned_number || "").trim() || "—";
  const portNumber = (primary?.port_number || "").trim() || "—";
  const email = (record?.email || primary?.user_email || "").trim() || "—";
  const site = (record?.site || "").trim() || "—";
  const userCode = (record?.user_code || primary?.user_code || "").trim() || "—";
  const operator = (primary?.mobile_operator || "").trim() || "—";

  const allChecked = BUYER_CONFIRMATION_POINTS.every((p) => checked[p.key]);
  const hasSelection = isBulk
    ? selectedPorts.length > 0
    : portList.length > 0;
  const canConfirm = isStaff
    ? hasSelection && !submitting
    : allChecked && hasSelection && !submitting;

  const togglePoint = (key) => {
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePort = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const removePort = (id) => {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const toggleSelectAllListed = () => {
    if (allListedSelected) {
      const listedSet = new Set(listedIds);
      setSelectedIds((prev) => prev.filter((id) => !listedSet.has(id)));
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      listedIds.forEach((id) => next.add(id));
      return [...next];
    });
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isBulk) {
        await onConfirm?.(selectedPorts);
      } else {
        await onConfirm?.();
      }
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to request conversion"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal fade show"
      style={{ display: "block", background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-dialog-scrollable" onClick={stop}>
        <div className="modal-content" style={{ fontSize: "1rem" }}>
          <div className="modal-header">
            <h5 className="modal-title">
              {isBulk ? "Bulk convert to TT OTP" : "Convert to TT OTP"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={submitting}
            />
          </div>
          <div className="modal-body">
            <p className="mb-3">
              {isBulk
                ? "Search by number or email, then select SIMs to convert to TT OTP."
                : "Request conversion of this SIM to a TT OTP number."}
            </p>

            {isBulk ? (
              <div className="mb-3">
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search by number or email…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={submitting || portList.length === 0}
                  autoFocus
                />

                <div className="mt-2">
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                    <div className="small fw-semibold">{listedLabel}</div>
                    {listedPorts.length > 0 && (
                      <label
                        className="d-flex align-items-center gap-1 mb-0 small"
                        style={{ cursor: submitting ? "default" : "pointer" }}
                      >
                        <input
                          ref={selectAllRef}
                          className="form-check-input m-0"
                          type="checkbox"
                          checked={allListedSelected}
                          onChange={toggleSelectAllListed}
                          disabled={submitting}
                          aria-label={selectAllLabel}
                        />
                        {selectAllLabel}
                      </label>
                    )}
                  </div>
                  <div
                    className="border rounded"
                    style={{ maxHeight: 220, overflowY: "auto" }}
                  >
                    {portList.length === 0 ? (
                      <div className="text-muted small p-2">
                        No eligible gateway numbers to convert.
                      </div>
                    ) : listedPorts.length === 0 ? (
                      <div className="text-muted small p-2">
                        No matching numbers or emails.
                      </div>
                    ) : (
                      listedPorts.map((p) => {
                        const checkedPort = selectedIds.includes(p.id);
                        return (
                          <label
                            key={p.id}
                            className="d-flex align-items-start gap-2 px-2 py-2 mb-0"
                            style={{
                              cursor: "pointer",
                              borderBottom: "1px solid #eee",
                              background: checkedPort ? "#fff8e6" : undefined,
                            }}
                          >
                            <input
                              className="form-check-input mt-1"
                              type="checkbox"
                              checked={checkedPort}
                              onChange={() => togglePort(p.id)}
                              disabled={submitting}
                            />
                            <PortPickLabel port={p} details />
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="mt-2">
                  <div className="small fw-semibold mb-1">
                    Selected ({selectedPorts.length})
                  </div>
                  {selectedPorts.length === 0 ? (
                    <div className="text-muted small">None selected yet.</div>
                  ) : (
                    <div className="border rounded">
                      {visibleSelected.map((p) => (
                        <div
                          key={p.id}
                          className="d-flex align-items-center justify-content-between gap-2 px-2 py-2"
                          style={{ borderBottom: "1px solid #eee" }}
                        >
                          <PortPickLabel port={p} />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary py-0 px-1"
                            onClick={() => removePort(p.id)}
                            disabled={submitting}
                            title="Remove"
                            aria-label={`Remove ${p.assigned_number || p.id}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {extraSelectedCount > 0 && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm w-100 text-start py-2"
                          onClick={() => setShowAllSelected((openMore) => !openMore)}
                        >
                          {showAllSelected
                            ? "Show less"
                            : `Show more (${extraSelectedCount})`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-2 rounded mb-3" style={{ background: "#f8f9fa" }}>
                <div><strong>Number:</strong> {number}</div>
                {portNumber !== "—" && (
                  <div><strong>Port:</strong> {portNumber}</div>
                )}
                <div><strong>Email:</strong> {email}</div>
                <div><strong>Site:</strong> {site}</div>
                <div><strong>User code:</strong> {userCode}</div>
                <div><strong>Operator:</strong> {operator}</div>
              </div>
            )}

            {!isStaff && (
              <div className="mb-0">
                <div className="fw-semibold mb-2">Please confirm all of the following:</div>
                {BUYER_CONFIRMATION_POINTS.map((point) => (
                  <label
                    key={point.key}
                    className="form-check d-flex align-items-start gap-2 mb-2"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      className="form-check-input mt-1"
                      type="checkbox"
                      checked={!!checked[point.key]}
                      onChange={() => togglePoint(point.key)}
                      disabled={submitting}
                    />
                    <span className="form-check-label">
                      {isBulk ? point.bulk : point.single}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {error && (
              <div className="alert alert-danger py-2 small mt-3 mb-0">{error}</div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              {submitting
                ? "Requesting…"
                : isBulk
                ? `Confirm (${selectedPorts.length})`
                : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
